export const useCredits = () => {
  const UserStore = useUserStore()
  const { credits: storeCredits, balance: storeBalance, currency: storeCurrency, grants: storeGrants, transactions: storeTransactions } = storeToRefs(UserStore)
  const { setCredits, setBalance, setCurrency, setGrants, setTransactions } = UserStore
  
  const loading = ref(false)
  const error = ref<string | null>(null)
  const creditsData = ref<any>(null) // Store the full API response

  const fetchCredits = async () => {
    if (loading.value) return
    
    loading.value = true
    error.value = null
    
    try {
      console.log('🔄 Fetching credits...')
      
      const response = await $fetch('/api/billing/credits', {
        method: 'POST'
      }) as any
      
      if (response?.error) {
        error.value = response.error
        return
      }
      
      // Store the full response for components that need additional data
      creditsData.value = response
      
      // Update userStore with the core data
      setCredits(response?.credits || 0)
      setBalance(response?.balance || 0)
      setCurrency(response?.currency || 'aud')
      setGrants(response?.grants || [])
      setTransactions(response?.transactions || [])
      
      console.log('✅ Credits fetched:', {
        credits: response?.credits,
        customer_source: response?.customer_source,
        data_source: response?.data_source
      })
      
    } catch (err) {
      console.error('❌ Error fetching credits:', err)
      error.value = 'Failed to fetch credits'
    } finally {
      loading.value = false
    }
  }

  const purchaseCredits = async (amount: number, options: {
    description?: string
    metadata?: Record<string, any>
    return_route?: string
    return_params?: Record<string, any>
    return_query?: Record<string, any>
  } = {}) => {
    loading.value = true
    error.value = null
    
    try {
      console.log('💳 Purchasing credits:', amount)
      
      const response = await $fetch('/api/billing/purchase-credits', {
        method: 'POST',
        body: { 
          amount, 
          description: options.description,
          metadata: options.metadata,
          return_route: options.return_route,
          return_params: options.return_params,
          return_query: options.return_query
        }
      }) as any
      
      if (!response?.success) {
        error.value = response?.error || 'Failed to create credit purchase'
        return null
      }

      console.log('✅ Credit purchase created:', {
        session_id: response.sessionId,
        customer: response.customer,
        customer_source: response.customerSource,
        credits: response.credits
      })

      return response
      
    } catch (err) {
      console.error('❌ Error purchasing credits:', err)
      error.value = err instanceof Error ? err.message : 'Failed to purchase credits'
      return null
    } finally {
      loading.value = false
    }
  }

  const consumeCredits = async (amount: number, description?: string, metadata: Record<string, any> = {}) => {
    loading.value = true
    error.value = null
    
    try {
      console.log('⚡ Consuming credits via budget-capped model:', amount)
      
      // Use budget-capped consumption as the default for current billing model
      const response = await $fetch('/api/billing/consume-credits-budget-capped', {
        method: 'POST',
        body: {
          amount,
          description,
          metadata
        }
      }) as any
      
      if (!response?.success) {
        error.value = response?.error || response?.message || 'Failed to consume credits'
        return response // Return the response even if not successful for error handling
      }

      console.log('✅ Credits consumed via budget-capped model:', {
        meter_event_id: response.usage_tracking?.meter_event_id,
        amount_tracked: response.usage_tracking?.amount_tracked,
        budget_utilization: response.budget_status?.budget_utilization
      })

      // Refresh credits after consumption
      await fetchCredits()

      return response
      
    } catch (err) {
      console.error('❌ Error consuming credits:', err)
      error.value = err instanceof Error ? err.message : 'Failed to consume credits'
      return { success: false, error: err instanceof Error ? err.message : 'Failed to consume credits' }
    } finally {
      loading.value = false
    }
  }

  // Immediate credit consumption for real-time balance updates (deprecated in budget-capped model)
  const consumeCreditsImmediate = async (amount: number, description?: string, metadata: Record<string, any> = {}) => {
    console.warn('⚠️ consumeCreditsImmediate is deprecated in Budget-Capped Postpaid model. Use consumeCredits() instead.')
    return consumeCredits(amount, description, { ...metadata, _legacy_immediate: true })
  }

  // Formatted credits display with currency conversion
  const formatCredits = computed(() => {
    const creditAmount = storeCredits.value
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: storeCurrency.value?.toUpperCase() || 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(creditAmount)
  })

  // Credit balance as integer credits (for display)
  const creditBalance = computed(() => {
    // Convert dollars to credits using 10 credits per dollar
    return Math.floor(storeCredits.value * 10)
  })

  // Check if user has credits
  const hasCredits = computed(() => storeCredits.value > 0)

  // Check if user has sufficient credits for a specific amount
  const hasSufficientCredits = (requiredAmount: number) => {
    return computed(() => storeCredits.value >= requiredAmount)
  }

  // Recent transactions (filtered and formatted)
  const recentTransactions = computed(() => {
    return storeTransactions.value?.slice(0, 10) || []
  })

  // Credit grants summary
  const creditGrants = computed(() => {
    return storeGrants.value || []
  })

  // Burndown analytics (if available from API response)
  const burndownAnalytics = computed(() => {
    return creditsData.value?.burndown || null
  })

  // Customer details (for debugging)
  const customerDetails = computed(() => {
    return {
      customer_id: creditsData.value?.customer_id,
      customer_source: creditsData.value?.customer_source,
      data_source: creditsData.value?.data_source,
      is_individual: creditsData.value?.is_individual
    }
  })

  // Handle payment return from URL parameters
  const handlePaymentReturn = () => {
    if (typeof window === 'undefined') return null
    
    const url = new URL(window.location.href)
    const paymentStatus = url.searchParams.get('payment_status')
    const sessionId = url.searchParams.get('session_id')
    const creditsPurchased = url.searchParams.get('credits_purchased')
    const creditPurchase = url.searchParams.get('credit_purchase')
    
    if (paymentStatus === 'success' && creditPurchase === 'true') {
      return {
        success: true,
        message: `Successfully purchased $${creditsPurchased} credits!`,
        session_id: sessionId,
        credits_purchased: creditsPurchased
      }
    }
    
    if (paymentStatus === 'cancelled' && creditPurchase === 'cancelled') {
      return {
        success: false,
        cancelled: true,
        message: 'Credit purchase was cancelled'
      }
    }
    
    return null
  }

  // Clean payment parameters from URL
  const cleanPaymentParams = () => {
    if (typeof window === 'undefined') return
    
    const url = new URL(window.location.href)
    const paramsToClean = [
      'payment_status', 
      'session_id', 
      'credits_purchased', 
      'credit_purchase'
    ]
    
    let hasChanges = false
    paramsToClean.forEach(param => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param)
        hasChanges = true
      }
    })
    
    if (hasChanges) {
      window.history.replaceState({}, '', url.toString())
    }
  }

  return {
    // State
    loading: readonly(loading),
    error: readonly(error),
    creditsData: readonly(creditsData),
    
    // Computed values
    credits: readonly(storeCredits),
    balance: readonly(storeBalance),
    currency: readonly(storeCurrency),
    formatCredits,
    creditBalance,
    hasCredits,
    hasSufficientCredits,
    recentTransactions,
    creditGrants,
    burndownAnalytics,
    customerDetails,
    
    // Actions
    fetchCredits,
    purchaseCredits,
    consumeCredits,
    consumeCreditsImmediate,
    handlePaymentReturn,
    cleanPaymentParams
  }
} 